import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

/**
 * SplitText Component
 * Animates text by splitting it into words and characters with staggered animations
 * Similar to animejs splitText functionality but using framer-motion
 * 
 * Usage:
 * <SplitText>Single line text</SplitText>
 * <SplitText lines={["Line 1", "Line 2"]} />
 * 
 * Animation sequence:
 * 1. Words animate first (staggered by wordDelay) - alternating up/down based on line
 * 2. Then characters animate (staggered randomly by charDelay) - same direction as their word
 */
export default function SplitText({ 
  children,
  lines, // Optional: array of strings for multiple lines
  className = '', 
  wordDelay = 0.125, // 125ms stagger between words
  charDelay = 0.01,   // 10ms stagger between chars
  duration = 0.65,    // 650ms duration
  ease = [0.43, 0.13, 0.23, 0.96], // inOut(3) cubic bezier approximation
  onComplete
}) {
  const [words, setWords] = useState([]);
  const [chars, setChars] = useState([]);
  const [isMounted, setIsMounted] = useState(false);
  const [shuffledChars, setShuffledChars] = useState([]);

  useEffect(() => {
    // Use lines prop if provided, otherwise extract from children
    let textLines = [];
    
    if (lines && Array.isArray(lines)) {
      textLines = lines.map((line, idx) => ({
        text: typeof line === 'string' ? line : line.toString(),
        lineIndex: idx
      }));
    } else if (children) {
      // Extract text from children
      const extractText = (node) => {
        if (typeof node === 'string') {
          return node;
        }
        if (Array.isArray(node)) {
          return node.map(child => extractText(child)).join('');
        }
        if (node && typeof node === 'object' && node.props) {
          if (node.props.children) {
            return extractText(node.props.children);
          }
        }
        return node?.toString() || '';
      };
      
      const text = extractText(children);
      textLines = [{ text, lineIndex: 0 }];
    }

    if (textLines.length === 0) return;

    // Split each line into words
    const wordElements = [];
    const charElements = [];
    let globalCharIndex = 0;

    textLines.forEach((line) => {
      const parts = line.text.split(/(\s+)/);
      
      parts.forEach((part, partIndex) => {
        if (!part.trim() && part !== ' ') return;

        const wordChars = part.split('');
        const wordCharElements = [];

        wordChars.forEach((char, charIndex) => {
          const charId = `char-${line.lineIndex}-${partIndex}-${charIndex}`;
          const charObj = {
            id: charId,
            char: char === ' ' ? '\u00A0' : char,
            lineIndex: line.lineIndex,
            wordIndex: wordElements.length,
            charIndex: globalCharIndex++
          };
          charElements.push(charObj);
          wordCharElements.push(charObj);
        });

        wordElements.push({
          id: `word-${line.lineIndex}-${partIndex}`,
          text: part,
          chars: wordCharElements,
          lineIndex: line.lineIndex,
          wordIndex: wordElements.length
        });
      });
    });

    setWords(wordElements);
    setChars(charElements);
    
    // Shuffle characters for random animation
    const shuffleArray = (array) => {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    };
    
    setShuffledChars(shuffleArray(charElements));
    setIsMounted(true);
  }, [children, lines]);

  // Calculate initial Y position based on line index (alternating)
  const getInitialY = (lineIndex) => {
    return lineIndex % 2 === 0 ? '-100%' : '100%';
  };

  // Calculate when character animation should start (skip word animation, start immediately)
  const getCharAnimationStart = () => {
    return 0; // Start character animation immediately, skipping word animation
  };

  if (!isMounted || words.length === 0) {
    return <span className={className}>{children}</span>;
  }

  // Group words by line for proper rendering
  const wordsByLine = words.reduce((acc, word) => {
    if (!acc[word.lineIndex]) acc[word.lineIndex] = [];
    acc[word.lineIndex].push(word);
    return acc;
  }, {});

  const charStartDelay = getCharAnimationStart();
  const totalLines = Object.keys(wordsByLine).length;

  return (
    <span className={`split-text ${className}`} style={{ display: 'block', width: '100%' }}>
      {Object.keys(wordsByLine).sort((a, b) => parseInt(a) - parseInt(b)).map((lineIndex) => {
        const lineWords = wordsByLine[lineIndex];
        const initialY = getInitialY(parseInt(lineIndex));
        const lineNum = parseInt(lineIndex);
        const isLastLine = lineNum === totalLines - 1;

        return (
          <span 
            key={lineIndex} 
            className={lineNum === 1 ? 'text-accent-200' : ''}
            style={{ 
              display: 'block', 
              width: '100%',
              lineHeight: 'inherit',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              position: 'relative',
              marginBottom: isLastLine ? '0' : '0'
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: isLastLine ? 'flex-start' : 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                width: '100%',
                height: isLastLine ? 'auto' : '2em',
                minHeight: isLastLine ? '1em' : '2em',
                maxHeight: isLastLine ? '1.2em' : '2em',
                lineHeight: 'inherit',
                position: 'relative',
                paddingBottom: '0',
                marginBottom: '0'
              }}
            >
            {lineWords.map((wordObj) => (
              <span
                key={wordObj.id}
                className="split-word"
                style={{ 
                  display: 'inline-block',
                  overflow: 'hidden',
                  whiteSpace: 'pre',
                  marginRight: wordObj.text === ' ' ? '0.25em' : '0'
                }}
              >
                <motion.span
                  initial={{ y: initialY }}
                  animate={{ y: '0%' }}
                  transition={{
                    duration: 0, // Skip word animation
                    ease,
                    delay: 0
                  }}
                  style={{ 
                    display: 'inline-block',
                    position: 'relative'
                  }}
                >
                  {wordObj.chars.map((charObj) => {
                    // Find position in shuffled array for random delay
                    const shuffledIndex = shuffledChars.findIndex(
                      c => c.id === charObj.id
                    );
                    const randomDelay = shuffledIndex !== -1 
                      ? shuffledIndex * charDelay 
                      : charObj.charIndex * charDelay;

                    return (
                      <motion.span
                        key={charObj.id}
                        initial={{ y: initialY }}
                        animate={{ y: '0%' }}
                        transition={{
                          duration: duration * 0.6,
                          ease,
                          delay: charStartDelay + randomDelay
                        }}
                        style={{ 
                          display: 'inline-block',
                          overflow: 'hidden'
                        }}
                        onAnimationComplete={() => {
                          if (wordObj.wordIndex === words.length - 1 && 
                              charObj.charIndex === chars.length - 1 && 
                              onComplete) {
                            onComplete();
                          }
                        }}
                      >
                        {charObj.char}
                      </motion.span>
                    );
                  })}
                </motion.span>
              </span>
            ))}
            </span>
          </span>
        );
      })}
    </span>
  );
}
